import React, { type JSX, useRef } from "react";

import { IFuzzyDurationField } from "../models/formFields";
import { FormInputFieldWrapper } from "./FormInputFieldWrapper";
import { useFormField } from "./hooks/useFormField";

const pad = (num: number): string => {
    return `0${num}`.slice(-2);
};

const NUM_YEARS = 100;
const NUM_MONTHS = 12;

export const FormFuzzyDurationSelect = <T extends string>(
    props: IFuzzyDurationField<T>,
): React.ReactNode => {
    const { type = "fuzzy-duration" } = props;

    // Don't pass inputRef to useFormField since this component manages multiple refs internally
    const { inputRef: _inputRef, ...propsWithoutInputRef } = props;
    const field = useFormField<T, HTMLInputElement>(propsWithoutInputRef);
    const monthSelectRef = useRef<HTMLSelectElement>(null);
    const yearSelectRef = useRef<HTMLSelectElement>(null);

    const yearOptions = (): React.JSX.Element[] => {
        const options: JSX.Element[] = [];
        for (let years = 0; years < NUM_YEARS; years++) {
            options.push(
                <option key={years} value={pad(years)}>
                    {years === 1 ? `${years} year` : `${years} years`}
                </option>,
            );
        }
        return options;
    };

    const monthOptions = (): React.JSX.Element[] => {
        const options: JSX.Element[] = [];
        for (let months = 0; months < NUM_MONTHS; months++) {
            options.push(
                <option key={months} value={pad(months)}>
                    {months === 1 ? `${months} month` : `${months} months`}
                </option>,
            );
        }
        return options;
    };

    const computeInputValue = (): string => {
        if (!monthSelectRef.current || !yearSelectRef.current) {
            return "0000";
        }
        return `${monthSelectRef.current.value}${yearSelectRef.current.value}`;
    };

    const buildChangeEvent = ():
        | React.FormEvent<HTMLInputElement>
        | undefined => {
        const target = field.inputRef.current;
        if (!target) {
            return;
        }

        const event: React.FormEvent<HTMLInputElement> = {
            bubbles: true,
            currentTarget: target,
            cancelable: false,
            defaultPrevented: true,
            eventPhase: 0,
            isTrusted: true,
            nativeEvent: new Event("InputEvent"),
            preventDefault: () => {},
            isDefaultPrevented: () => {
                return true;
            },
            stopPropagation: () => {},
            isPropagationStopped: () => {
                return true;
            },
            persist: () => {},
            target: target,
            timeStamp: new Date().getTime(),
            type: "onChange",
        };
        return event;
    };

    const handleSelectChange = () => {
        // Update hidden input's value
        const input = field.inputRef.current;
        if (!input) {
            return;
        }
        // Compute and set the input value from the values of the two selects
        input.value = computeInputValue();
        field.valueRef.current = input.value;
        // Trigger a change event
        const event = buildChangeEvent();
        if (!event) {
            return;
        }
        field.handleChange(event);
    };

    const baseInputProps = field.inputProps;
    // Get props for the hidden input. Never set a value because we need it to be an unmanaged input.
    const inputProps = {
        ...baseInputProps,
        type: "hidden",
        onChange: undefined,
        onBlur: undefined,
        onFocus: undefined,
        value: undefined,
    };

    // Make changing the select elements update the value of the hidden input and then
    // use the input to send a synthetic change event.
    // Note: We only pass safe props that work with select elements
    const selectProps = {
        "id": baseInputProps.id,
        "name": baseInputProps.name,
        "className": baseInputProps.className,
        "disabled": baseInputProps.disabled,
        "required": baseInputProps.required,
        "value": baseInputProps.value || "",
        "onChange": handleSelectChange,
        "aria-required": baseInputProps["aria-required"],
        "aria-describedby": baseInputProps["aria-describedby"],
        "aria-expanded": baseInputProps["aria-expanded"],
    };

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <div>
                <select
                    ref={yearSelectRef}
                    {...selectProps}
                    name={`${selectProps.name}_years`}
                    data-testid={`${selectProps.name}_years`}
                    value={String(selectProps.value).slice(-2)}
                >
                    {yearOptions()}
                </select>

                <select
                    ref={monthSelectRef}
                    {...selectProps}
                    name={`${selectProps.name}_months`}
                    data-testid={`${selectProps.name}_months`}
                    value={String(selectProps.value).slice(0, 2)}
                >
                    {monthOptions()}
                </select>

                <input ref={field.inputRef} {...inputProps} />
            </div>
        </FormInputFieldWrapper>
    );
};
